// frontend/src/pages/Posts.tsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
const LazyVerificationBadge = React.lazy(() => import('../components/VerificationBadge'));
import {
  Heart,
  MessageCircle,
  Send,
  Trash2,
  Edit,
  X,
  Image as ImageIcon,
  Plus,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Share,
  Calendar,
  ShoppingBag,
  Briefcase,
  Tag as TagIcon,
  MapPin,
  RefreshCcw,
  MoreVertical,
} from 'lucide-react';
import { containsOffensive } from '../utils/safety';
import Avatar from '../components/Avatar';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import EventDetailModal from '../components/EventDetailModal';
import CreateEventModal from '../components/CreateEventModal';
import ServiceDetailModal from '../components/ServiceDetailModal';
import ProductDetailModal from '../components/ProductDetailModal';
import VenueDetailModal from '../components/VenueDetailModal';

dayjs.extend(relativeTime);

const API = API_URL.replace(/\/api$/, '');

interface Post {
  _id: string;
  author: {
    _id: string;
    username: string;
    avatar?: string;
    email: string;
  };
  caption: string;
  imageUrl: string;
  likes: string[];
  comments: Array<{
    _id: string;
    user: {
      _id: string;
      username: string;
      avatar?: string;
    };
    text: string;
    likes?: string[];
    replies?: Array<{
      _id?: string;
      user: {
        _id: string;
        username: string;
        avatar?: string;
      };
      text: string;
      likes?: string[];
      createdAt: string;
    }>;
    createdAt: string;
  }>;
  tags: string[];
  location: string;
  createdAt: string;
  updatedAt: string;
  captionEditedAt?: string;
}

type FeedTab = 'posts' | 'events' | 'services' | 'products';

interface UnifiedItem {
  kind: 'event' | 'service' | 'product' | 'venue';
  id: string;
  title: string;
  subtitle?: string;
  imageUrl?: string;
  createdAt?: string;
  dateHint?: string; // for events
  priceHint?: string; // for services/products
  user?: { _id: string; username: string; avatar?: string };
  raw: any;
}

export default function Posts({ token, currentUserId, onShowProfile, onNavigate }: any) {
    // Independent state for each feed
    const [serviceFeed, setServiceFeed] = useState<UnifiedItem[]>([]);
    const [servicesLoading, setServicesLoading] = useState<boolean>(false);
    const [servicePage, setServicePage] = useState<number>(1);
    const [hasMoreServices, setHasMoreServices] = useState<boolean>(true);
    const [productFeed, setProductFeed] = useState<UnifiedItem[]>([]);
    const [productsLoading, setProductsLoading] = useState<boolean>(false);
    const [productPage, setProductPage] = useState<number>(1);
    const [hasMoreProducts, setHasMoreProducts] = useState<boolean>(true);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<FeedTab>('posts');
  const [sortMode, setSortMode] = useState<'prioritized' | 'newest' | 'random'>('prioritized');
  const [searchText, setSearchText] = useState<string>('');
  const [nearMeEnabled, setNearMeEnabled] = useState<boolean>(false);
  const [locating, setLocating] = useState<boolean>(false);
  const [userLoc, setUserLoc] = useState<{ lat: number; lng: number } | null>(null);
  const [nearRadiusKm, setNearRadiusKm] = useState<number>(() => {
    try {
      const raw = localStorage.getItem('auralink-near-radius-km');
      const n = raw ? parseInt(raw, 10) : 25;
      return [5, 10, 25, 50].includes(n) ? n : 25;
    } catch {
      return 25;
    }
  });
  const [geoError, setGeoError] = useState<string | null>(null);
  const [approxLoc, setApproxLoc] = useState<boolean>(false);
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);

  useEffect(() => {
    // Check geolocation permission state to surface better errors
    (async () => {
      try {
        const res = await (navigator as any).permissions?.query?.({ name: 'geolocation' as any });
        setPermissionState(res?.state ?? null);
      } catch {
        setPermissionState(null);
      }
    })();
  }, []);
  // Persist sort mode on change for consistent UX
  useEffect(() => {
    try { localStorage.setItem('auralink-feed-sort', sortMode); } catch {}
  }, [sortMode]);
  // Restore sort mode from URL or localStorage
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const s = (params.get('sort') || '').toLowerCase();
      if (s === 'new' || s === 'newest') {
        setSortMode('newest');
        return;
      }
      if (s === 'prioritized') {
        setSortMode('prioritized');
        return;
      }
      if (s === 'random') {
        setSortMode('random');
      } else {
        const saved = localStorage.getItem('auralink-feed-sort');
        if (saved === 'newest' || saved === 'prioritized' || saved === 'random') setSortMode(saved as any);
      }
      const q = params.get('q');
      if (q) setSearchText(q);
      else {
        try {
          const savedQ = localStorage.getItem('auralink-feed-q');
          if (savedQ) setSearchText(savedQ);
        } catch {}
      }
    } catch {}
  }, []);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newPost, setNewPost] = useState({ caption: '', imageUrl: '', location: '', tags: '' });
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [expandedCaptions, setExpandedCaptions] = useState<Record<string, boolean>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(-1);
  const imageList = React.useMemo(
    () => posts.filter((p) => !!p.imageUrl).map((p) => p.imageUrl),
    [posts],
  );

  // EVENTS FEED STATE
  const [eventFeed, setEventFeed] = useState<UnifiedItem[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);
  const [eventPage, setEventPage] = useState<number>(1);
  const [svcPage, setSvcPage] = useState<number>(1);
  const [mktPage, setMktPage] = useState<number>(1);
  const [venuePage, setVenuePage] = useState<number>(1);
  const [hasMoreEvents, setHasMoreEvents] = useState<boolean>(true);
  const [revCityById, setRevCityById] = useState<Record<string, string>>({});
  const revCacheRef = useRef<Map<string, string>>(new Map());
  
  // Persist tab across refresh and back/forward via URL query (?tab=events)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('tab');
      if (t === 'events' || t === 'posts' || t === 'services' || t === 'products') {
        setTab(t as FeedTab);
        return;
      }
      // No explicit tab in URL G�� default to posts as landing
      setTab('posts');
      try { localStorage.setItem('auralink-last-feed-tab', 'posts'); } catch {}
    } catch {}
  }, []);
  
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('tab', tab);
      // Persist sort mode and query in URL
      params.set('sort', sortMode === 'newest' ? 'new' : sortMode === 'prioritized' ? 'prioritized' : 'random');
      if (searchText && searchText.trim()) params.set('q', searchText.trim());
      else params.delete('q');
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      const prevState = window.history.state || {};
      // Push a history entry so Back returns to previous tab/view
      window.history.pushState({ ...prevState, tab, sort: sortMode, q: searchText }, '', newUrl);
      // Increment in-session history depth to avoid leaving web on Back
      try {
        const raw = sessionStorage.getItem('auralink-history-count') || '1';
        const n = parseInt(raw, 10);
        const next = (Number.isFinite(n) && n > 0 ? n : 1) + 1;
        sessionStorage.setItem('auralink-history-count', String(next));
      } catch {}
      // Persist last tab for next refresh alternation
      try { localStorage.setItem('auralink-last-feed-tab', tab); } catch {}
    } catch {}
  }, [tab, sortMode, searchText]);
  
  useEffect(() => {
    const onPop = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const t = params.get('tab');
        if (t === 'events' || t === 'posts' || t === 'services' || t === 'products') setTab(t as FeedTab);
        else setTab('posts');
        const s = (params.get('sort') || '').toLowerCase();
        if (s === 'new' || s === 'newest') setSortMode('newest');
        else if (s === 'prioritized') setSortMode('prioritized');
        else if (s === 'random') setSortMode('random');
        const q = params.get('q') || '';
        setSearchText(q);
      } catch {}
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Persist sort mode changes to localStorage and URL (handled above via [tab, sortMode] effect)
  useEffect(() => {
    try { localStorage.setItem('auralink-feed-sort', sortMode); } catch {}
  }, [sortMode]);
  useEffect(() => {
    try { localStorage.setItem('auralink-feed-q', searchText); } catch {}
  }, [searchText]);

  // Detail modals state
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [selectedService, setSelectedService] = useState<any | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<any | null>(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [openItemMenuKey, setOpenItemMenuKey] = useState<string | null>(null);
  const [userTierById, setUserTierById] = useState<Record<string, string>>({});
  const [venueTierById, setVenueTierById] = useState<Record<string, string>>({});

  function openImage(url: string) {
    setPreviewImage(url);
    const idx = imageList.indexOf(url);
    setPreviewIndex(idx >= 0 ? idx : imageList.findIndex((u) => u === url));
  }
  
  function renderEventsTab() {
    // Determine which kinds to show based on the active tab
    const filtered = tab === 'events'
      ? visibleEvents.filter((i) => i.kind === 'event' || i.kind === 'venue')
      : tab === 'services'
        ? visibleEvents.filter((i) => i.kind === 'service')
        : tab === 'products'
          ? visibleEvents.filter((i) => i.kind === 'product')
          : visibleEvents;
    return (
      <div className="space-y-4">
        {eventsLoading || servicesLoading || productsLoading && filtered.length === 0 ? (
          <div className="space-y-6">
            {[1, 2].map((i) => (
              <div key={i} className="rounded-2xl p-6 animate-pulse themed-card">
                <div className="h-48 themed-card"></div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ImageIcon className="w-14 h-14 text-theme-secondary mx-auto mb-3" />
            <p className="text-theme-secondary">{tab === 'events' ? 'No events or venues yet' : tab === 'services' ? 'No services yet' : tab === 'products' ? 'No products yet' : 'No items yet'}</p>
          </div>
        ) : (
          <>
            {filtered.map((item) => (
              <div
                key={`${item.kind}:${item.id}`}
                className="rounded-xl themed-card overflow-hidden hover:shadow-md transition-all cursor-pointer"
                style={{}}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (item.kind === 'event') setSelectedEvent(item.raw);
                  else if (item.kind === 'service') setSelectedService(item.raw);
                  else if (item.kind === 'product') setSelectedProduct(item.raw);
                  else setSelectedVenue(item.raw);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (item.kind === 'event') setSelectedEvent(item.raw);
                    else if (item.kind === 'service') setSelectedService(item.raw);
                    else if (item.kind === 'product') setSelectedProduct(item.raw);
                    else setSelectedVenue(item.raw);
                  }
                }}
              >
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 relative">
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={
                        item.user?.avatar
                          ? item.user.avatar.startsWith('http')
                            ? item.user.avatar
                            : API + (item.user.avatar.startsWith('/') ? item.user.avatar : '/uploads/' + item.user.avatar)
                          : 'https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff'
                      }
                      className="w-9 h-9 rounded-full object-cover cursor-pointer"
                      alt={item.user?.username || 'User'}
                      onClick={(e) => { e.stopPropagation(); item.user && onShowProfile?.(item.user); }}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0 font-semibold text-heading">
                        <span className="truncate min-w-0 max-w-[10rem] sm:max-w-[14rem]">
                          {item.user?.username || (item.kind === 'product' ? 'Seller' : 'Organizer')}
                        </span>
                      </div>
                      {item.createdAt && (
                        <div className="text-xs text-theme-secondary">{dayjs(item.createdAt).fromNow()}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                      {item.kind === 'product' && <TypePill kind={item.kind} />}
                      {item.kind === 'product' && item.priceHint && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border" style={{ borderColor: 'var(--border)' }}>
                          <TagIcon className="w-3.5 h-3.5" /> {item.priceHint}
                        </span>
                      )}
                    {/* Three-dots options menu (like posts) */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const key = `${item.kind}:${item.id}`;
                          setOpenItemMenuKey((k) => (k === key ? null : key));
                        }}
                        className="hover:opacity-80"
                        title="More"
                        aria-haspopup="menu"
                        aria-expanded={openItemMenuKey === `${item.kind}:${item.id}`}
                      >
                        <MoreVertical className="w-5 h-5 text-theme-secondary" />
                      </button>
                      {openItemMenuKey === `${item.kind}:${item.id}` && (
                        <div
                          className="absolute right-0 top-8 z-20 w-60 rounded-xl shadow-xl border"
                          style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                          onClick={(e) => e.stopPropagation()}
                          role="menu"
                          aria-label="Item options"
                        >
                          <button
                            onClick={() => {
                              setOpenItemMenuKey(null);
                              if (item.kind === 'event') setSelectedEvent(item.raw);
                              else if (item.kind === 'service') setSelectedService(item.raw);
                              else if (item.kind === 'product') setSelectedProduct(item.raw);
                              else setSelectedVenue(item.raw);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-black/5 dark:hover:bg-white/10"
                            role="menuitem"
                          >
                            <Share className="w-4 h-4" /> Open Details
                          </button>
                          <button
                            onClick={() => {
                              setOpenItemMenuKey(null);
                              handleShareUnifiedItem(item);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-black/5 dark:hover:bg-white/10"
                            role="menuitem"
                          >
                            <Share className="w-4 h-4" /> Share {item.kind.charAt(0).toUpperCase() + item.kind.slice(1)}
                          </button>
                          <div className="border-t" style={{ borderColor: 'var(--border)' }} />
                          <button
                            onClick={() => setOpenItemMenuKey(null)}
                            className="w-full px-4 py-2.5 text-xs text-theme-secondary text-left hover:bg-black/5 dark:hover:bg-white/10"
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Media */}
                {item.imageUrl && (
                  <div className="relative w-full h-56 sm:h-72 md:h-[22rem] overflow-hidden">
                    <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/0 to-transparent" />
                    {item.kind === 'event' && item.dateHint && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-600/85 text-white shadow-sm">
                        <Calendar className="w-3.5 h-3.5" /> {item.dateHint}
                      </span>
                    )}
                    {item.kind === 'service' && item.priceHint && (
                      <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-cyan-600/85 text-white shadow-sm">
                        <TagIcon className="w-3.5 h-3.5" /> {item.priceHint}
                      </span>
                    )}
                  </div>
                )}

                {/* Body */}
                <div className="p-3 space-y-2">
                  <div className="text-lg font-bold text-heading">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="min-w-0 flex-1 truncate">{item.title}</span>
                    </div>
                  </div>
                  {item.subtitle && <div className="text-sm text-theme-secondary">{item.subtitle}</div>}
                  {item.kind === 'service' && (item.raw as any)?.professionalArea && (
                    <div className="mt-0.5 text-xs capitalize text-theme-secondary">{String((item.raw as any).professionalArea).replace(/-/g, ' ')}</div>
                  )}
                  {item.kind === 'service' && (item.raw as any)?.aboutShort && (
                    <div className="mt-1 text-xs text-theme-secondary line-clamp-2">{String((item.raw as any).aboutShort)}</div>
                  )}
                  {(item.kind === 'event' || item.kind === 'venue') && (() => {
                    const loc = (item.raw && (item.raw as any).location) || {};
                    const txt = loc.city || loc.name || loc.address;
                    const c = loc.coordinates;
                    let display = txt as string | null;
                    if (!display && c && typeof c.lat === 'number' && typeof c.lng === 'number') {
                      const id = String((item.raw && (item.raw as any)._id) || item.id || '');
                      const cachedCity = id ? revCityById[id] : undefined;
                      if (cachedCity) display = cachedCity;
                      else {
                        queueReverseGeocode(id, c.lat, c.lng, revCacheRef.current, setRevCityById);
                        display = `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
                      }
                    }
                    const dist = nearMeEnabled && userLoc && c && typeof c.lat === 'number' && typeof c.lng === 'number'
                      ? haversineKm(userLoc.lat, userLoc.lng, c.lat, c.lng)
                      : null;
                    return display ? (
                      <div className="text-xs text-theme-secondary inline-flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" /> {display} {dist != null ? <span className="ml-1">• {dist.toFixed(1)} km away</span> : null}
                      </div>
                    ) : null;
                  })()}
                  {/* Verification badge moved to bottom for consistency and to avoid overflow */}
                  <div className="pt-1">
                    {item.kind === 'venue' ? (
                      (() => {
                        const vid = String(((item.raw as any)?._id) || item.id || '');
                        const vTier = venueTierById[vid] as any;
                        if (vTier && vTier !== 'none') {
                          return (
                            <React.Suspense fallback={null}>
                              <LazyVerificationBadge kind="venue" tier={vTier} />
                            </React.Suspense>
                          );
                        }
                        return <TypePill kind="venue" />;
                      })()
                    ) : item.user ? (
                      <React.Suspense fallback={null}>
                        <LazyVerificationBadge
                          kind="user"
                          tier={userTierById[String((item.user as any)._id || (item.user as any).id || '')] as any}
                        />
                      </React.Suspense>
                    ) : null}
                  </div>
                  {/* Whole card is clickable; button row removed for a cleaner card */}
                </div>
              </div>
            ))}

            {hasMoreEvents && (
              <div className="flex justify-center pt-2">
                <button
                  className="px-4 py-2 rounded-xl border text-sm"
                  style={{ borderColor: 'var(--border)' }}
                  onClick={() => loadEventFeed(false)}
                  disabled={eventsLoading}
                >
                  {eventsLoading ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
  function closeImage() {
    setPreviewImage(null);
    setPreviewIndex(-1);
  }
  function prevImage() {
    if (!imageList.length) return;
    setPreviewIndex((i) => {
      const ni = (i + imageList.length - 1) % imageList.length;
      setPreviewImage(imageList[ni]);
      return ni;
    });
  }
  function nextImage() {
    if (!imageList.length) return;
    setPreviewIndex((i) => {
      const ni = (i + 1) % imageList.length;
      setPreviewImage(imageList[ni]);
      return ni;
    });
  }
  async function shareImage(url: string) {
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ title: 'TeaKonn Photo', url });
        return;
      }
    } catch {}
    try {
      await navigator.clipboard.writeText(url);
      alert('Image link copied');
    } catch {
      window.open(url, '_blank');
    }
  }
  async function downloadImage(url: string) {
    const filename = (url.split('/').pop() || 'image.jpg').split('?')[0];
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('fetch_failed');
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      // Fallback: open in a new tab if CORS blocks Blob download
      try {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.click();
      } catch {
        window.open(url, '_blank');
      }
    }
  }

  React.useEffect(() => {
    if (!previewImage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeImage();
      else if (e.key === 'ArrowLeft') prevImage();
      else if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewImage, imageList]);

  // Edit states
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editPostData, setEditPostData] = useState({ caption: '', location: '', tags: '' });
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');

  // Comments collapse state
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  const [showAllComments, setShowAllComments] = useState<Record<string, boolean>>({});
  const [expandedReplies, setExpandedReplies] = useState<Record<string, boolean>>({});
  const [commentBoxOpen, setCommentBoxOpen] = useState<Record<string, boolean>>({});
  const [expandedCommentText, setExpandedCommentText] = useState<Record<string, boolean>>({});
  const [expandedReplyText, setExpandedReplyText] = useState<
    Record<string, Record<number, boolean>>
  >({});

  // Reply state
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyingToUser, setReplyingToUser] = useState<string | null>(null);
  const REPLY_MAX = 200;
  const [editingReply, setEditingReply] = useState<{
    commentId: string;
    replyId: string;
    text: string;
  } | null>(null);

  // Cache for reply author details when API returns only IDs
  const [replyUserById, setReplyUserById] = useState<Record<string, { username?: string; avatar?: string }>>({});
  const inflightReplyUser = useRef<Set<string>>(new Set());

  function getReplyUser(u: any): { _id?: string; username?: string; avatar?: string } {
    if (!u) return {};
    if (typeof u === 'string') {
      const id = String(u);
      const cached = replyUserById[id] || {};
      return { _id: id, ...cached };
    }
    const id = String(u._id || u.id || '');
    if (u.username) return { _id: id, username: u.username, avatar: u.avatar };
    const cached = id ? replyUserById[id] || {} : {};
    return { _id: id, username: cached.username, avatar: cached.avatar };
  }

  async function fetchReplyUser(id: string) {
    try {
      const res = await axios.get(`${API}/api/users/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const u = res.data || {};
      setReplyUserById((m) => ({ ...m, [id]: { username: u.username, avatar: u.avatar } }));
    } catch (e) {
      setReplyUserById((m) => (m[id] ? m : { ...m, [id]: { username: 'User' } }));
    } finally {
      inflightReplyUser.current.delete(id);
    }
  }

  useEffect(() => {
    try {
      posts.forEach((p) => {
        (p.comments || []).forEach((c) => {
          (c.replies || []).forEach((r: any) => {
            const u = r.user;
            const id = typeof u === 'string' ? String(u) : String(u?._id || u?.id || '');
            const hasName = typeof u === 'object' && !!u?.username;
            if (id && !hasName && !replyUserById[id] && !inflightReplyUser.current.has(id)) {
              inflightReplyUser.current.add(id);
              fetchReplyUser(id);
            }
          });
        });
      });
    } catch {}
  }, [posts]);

  // Scroll to top state
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openPostMenuId, setOpenPostMenuId] = useState<string | null>(null);

  // Follow graph + rotation seed for feed ordering
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const [rotationSeed, setRotationSeed] = useState<number>(() => Date.now());

  // Derived, memoized display lists (avoid hooks inside conditionals)
  const visiblePosts = React.useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const filtered = q
      ? posts.filter((p) => {
          const text = [
            p.caption || '',
            p.author?.username || '',
            p.location || '',
            ...(p.tags || []),
          ].join(' ').toLowerCase();
          return text.includes(q);
        })
      : posts;
    if (sortMode === 'newest') {
      return [...filtered].sort(
        (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
      );
    }
    if (sortMode === 'prioritized') {
      return [...filtered].sort((a, b) => {
        const sa = scorePost(a);
        const sb = scorePost(b);
        if (sa !== sb) return sb - sa;
        const ta = dayjs(a.createdAt || 0).valueOf();
        const tb = dayjs(b.createdAt || 0).valueOf();
        if (ta !== tb) return tb - ta;
        return seededNoise(a._id) - seededNoise(b._id);
      });
    }
    // Random: deterministic per session; allow shuffle via rotationSeed
    return [...filtered].sort((a, b) => seededNoise(String(a._id), rotationSeed) - seededNoise(String(b._id), rotationSeed));
  }, [posts, sortMode, searchText, followingIds, rotationSeed]);

  const visibleEvents = React.useMemo(() => {
    const q = searchText.trim().toLowerCase();
    let filtered = q
      ? eventFeed.filter((i) => {
          const text = [
            i.title || '',
            i.subtitle || '',
            i.user?.username || '',
            i.priceHint || '',
            i.dateHint || '',
          ].join(' ').toLowerCase();
          return text.includes(q);
        })
      : eventFeed;

    // Apply Near Me filtering when enabled and we have coordinates.
    // Filter Events and Venues; include Services/Products only if coordinates exist.
    if (nearMeEnabled && userLoc) {
      filtered = filtered.filter((i) => {
        const raw: any = i.raw || {};
        const loc = raw.location || raw.loc || {};
        const c = loc.coordinates || loc.coords || raw.coordinates || null;
        const lat = c && typeof c.lat === 'number' ? c.lat : c && Array.isArray(c) && typeof c[1] === 'number' ? c[1] : undefined;
        const lng = c && typeof c.lng === 'number' ? c.lng : c && Array.isArray(c) && typeof c[0] === 'number' ? c[0] : undefined;
        if (typeof lat !== 'number' || typeof lng !== 'number') return false;
        const d = haversineKm(userLoc.lat, userLoc.lng, lat, lng);
        return d <= nearRadiusKm;
      });
    }
    const seed = rotationSeed;
    if (sortMode === 'newest') {
      return [...filtered].sort((a, b) => {
        const ta = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const tb = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return tb - ta;
      });
    }
    if (sortMode === 'prioritized') {
      return [...filtered].sort((a, b) => {
        const ta = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const tb = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        const ageA = Math.max(0, (Date.now() - ta) / (1000 * 60 * 60));
        const ageB = Math.max(0, (Date.now() - tb) / (1000 * 60 * 60));
        const recA = 1 / (1 + ageA / 24);
        const recB = 1 / (1 + ageB / 24);
        const fA = a.user?._id && followingIds.has(String(a.user._id)) ? 1 : 0;
        const fB = b.user?._id && followingIds.has(String(b.user._id)) ? 1 : 0;
        const kA = seededNoise('kind:' + a.kind, seed);
        const kB = seededNoise('kind:' + b.kind, seed);
        const iA = seededNoise(a.kind + ':' + a.id, seed);
        const iB = seededNoise(b.kind + ':' + b.id, seed);
        const sA = recA * 0.6 + fA * 0.25 + kA * 0.1 + iA * 0.05;
        const sB = recB * 0.6 + fB * 0.25 + kB * 0.1 + iB * 0.05;
        if (sA !== sB) return sB - sA;
        if (ta !== tb) return tb - ta;
        return (
          seededNoise(a.kind + ':' + a.id, seed) - seededNoise(b.kind + ':' + b.id, seed)
        );
      });
    }
    return [...filtered].sort(
      (a, b) =>
        seededNoise(a.kind + ':' + a.id, seed) - seededNoise(b.kind + ':' + b.id, seed),
    );
  }, [eventFeed, sortMode, searchText, followingIds, rotationSeed, nearMeEnabled, userLoc, nearRadiusKm]);

  // Prefetch city/town names via reverse geocoding for visible event items
  useEffect(() => {
    visibleEvents.forEach((item) => {
      if (item.kind !== 'event') return;
      const loc = (item.raw && (item.raw as any).location) || {};
      const hasText = !!(loc.city || loc.name || loc.address);
      const c = loc.coordinates;
      if (!hasText && c && typeof c.lat === 'number' && typeof c.lng === 'number') {
        const id = String((item.raw && (item.raw as any)._id) || item.id || '');
        if (!id) return;
        queueReverseGeocode(id, c.lat, c.lng, revCacheRef.current, setRevCityById);
      }
    });
  }, [visibleEvents]);

    // Fetch verification status for users and venues present in the visible feed
    useEffect(() => {
      try {
        const userIds = Array.from(
          new Set(
            visibleEvents
              .map((i: any) => String(i?.user?._id || i?.user?.id || ''))
              .filter((x) => !!x)
          )
        );
        const missingUsers = userIds.filter((id) => !(id in userTierById));
        if (missingUsers.length) {
          (async () => {
            const results = await Promise.all(
              missingUsers.map((id) => axios.get(`${API_URL}/verifications/status/user/${id}`).catch(() => null))
            );
            const updates: Record<string, string> = {};
            missingUsers.forEach((id, idx) => {
              const r = results[idx];
              const tier = r && (r as any).data ? ((r as any).data.tier || 'none') : 'none';
              updates[id] = tier;
            });
            setUserTierById((prev) => ({ ...prev, ...updates }));
          })();
        }

        const venueIds = Array.from(
          new Set(
            visibleEvents
              .filter((i: any) => i?.kind === 'venue')
              .map((i: any) => String((i?.raw?._id) || i?.id || ''))
              .filter((x) => !!x)
          )
        );
        const missingVenues = venueIds.filter((id) => !(id in venueTierById));
        if (missingVenues.length) {
          (async () => {
            const results = await Promise.all(
              missingVenues.map((id) => axios.get(`${API_URL}/verifications/status/venue/${id}`).catch(() => null))
            );
            const updates: Record<string, string> = {};
            missingVenues.forEach((id, idx) => {
              const r = results[idx];
              const tier = r && (r as any).data ? ((r as any).data.tier || 'none') : 'none';
              updates[id] = tier;
            });
            setVenueTierById((prev) => ({ ...prev, ...updates }));
          })();
        }
      } catch {}
    }, [visibleEvents]);

  // Load following once (used to prioritize followed authors)
  useEffect(() => {
    async function fetchFollowing() {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ids: string[] = (res.data?.following || []).map((u: any) => String(u._id || u.id));
        setFollowingIds(new Set(ids));
      } catch (e) {
        console.warn('Failed to fetch following; prioritization limited:', e);
      }
    }
    fetchFollowing();
  }, [token]);

  // Compute weighted score for a post (seeded per refresh for variety)
  function scorePost(post: Post, seed?: number): number {
    const now = dayjs();
    const created = dayjs(post.createdAt || post.updatedAt || 0);
    const ageHours = Math.max(0, now.diff(created, 'hour', true));
    // Recency: 1 at 0h, 0.5 at 24h, ~0.25 at 72h, asymptotically -> 0
    const recency = 1 / (1 + ageHours / 24);
    // Followed author boost
    const isFollowed = followingIds.has(String(post.author?._id));
    const followed = isFollowed ? 1 : 0;
    // Seeded, stable noise for this refresh
    const noise = seededNoise(String(post._id), seed);
    // Small author-specific jitter to avoid repetitive ordering
    const authorNoise = seededNoise('author:' + String(post.author?._id || 'unknown'), seed);
    // Weighted sum; ensure recency dominates, followed next, then jitter for variety
    return recency * 0.55 + followed * 0.3 + noise * 0.1 + authorNoise * 0.05;
  }

  function seededNoise(key: string, seed?: number): number {
    // Simple string hash to [0,1) using provided seed (falls back to rotationSeed)
    let h = 2166136261 ^ (seed ?? rotationSeed);
    for (let i = 0; i < key.length; i++) {
      h ^= key.charCodeAt(i);
      h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
    }
    // Convert to 0..1
    const u = (h >>> 0) / 4294967295;
    return u;
  }

  // Long-press actions replaced by explicit menu button (three dots)

  useEffect(() => {
    if (token) loadPosts(true);
  }, [token]);

  // Load Events/Services/Products feed when switching to events tab (first time)
  useEffect(() => {
    if (tab === 'events' && eventFeed.length === 0 && token) {
      loadEventFeed(true);
    }
    if (tab === 'services' && serviceFeed.length === 0 && token) {
      loadServiceFeed(true);
    }
    if (tab === 'products' && productFeed.length === 0 && token) {
      loadProductFeed(true);
    }
  }, [tab, token]);

  const currentUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If opened via a shared link, highlight and scroll to the post
  useEffect(() => {
    const highlightId = localStorage.getItem('auralink-highlight-post');
    if (!highlightId) return;
    const el = document.getElementById(`post-${highlightId}`);
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-cyan-500');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-cyan-500');
        try {
          localStorage.removeItem('auralink-highlight-post');
        } catch {}
      }, 2000);
    }, 300);
  }, [posts.length]);

  const [postsPage, setPostsPage] = useState<number>(1);
  const [hasMorePosts, setHasMorePosts] = useState<boolean>(true);

  async function loadPosts(initial = false) {
    try {
      setLoading(true);
      // Use a local seed to avoid relying on async state updates
      const seed = (Date.now() ^ Math.floor(Math.random() * 0x7fffffff)) >>> 0;
      const nextPage = initial ? 1 : postsPage + 1;
      const res = await axios.get(`${API}/api/posts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: nextPage, limit: 10 },
      });
      const incoming: Post[] = res.data.posts || [];
      // Merge with existing (if not initial) and deduplicate
      const merged = [...(initial ? [] : posts), ...incoming];
      const dedup = Array.from(new Map(merged.map((p) => [p._id, p])).values());
      // Sort according to mode
      const sorted =
        sortMode === 'newest'
          ? dedup.sort((a, b) => {
              const ta = dayjs(a.createdAt || 0).valueOf();
              const tb = dayjs(b.createdAt || 0).valueOf();
              return tb - ta;
            })
          : dedup.sort((a, b) => {
              const sa = scorePost(a, seed);
              const sb = scorePost(b, seed);
              if (sa !== sb) return sb - sa;
              // Stable tiebreaker by createdAt desc then id noise
              const ta = dayjs(a.createdAt || 0).valueOf();
              const tb = dayjs(b.createdAt || 0).valueOf();
              if (ta !== tb) return tb - ta;
              return seededNoise(a._id, seed) - seededNoise(b._id, seed);
            });
      setPosts(sorted);
      setPostsPage(nextPage);
      const cp = Number(res.data?.currentPage || nextPage);
      const tp = Number(res.data?.totalPages || nextPage);
      setHasMorePosts(cp < tp);
    } catch (err) {
      console.error('Failed to load posts:', err);
    } finally {
      setLoading(false);
    }
  }

  function makeUser(u: any) {
    if (!u) return undefined;
    if (typeof u === 'string') return { _id: u, username: 'Owner' } as any;
    return { _id: u._id || u.id, username: u.username, avatar: u.avatar };
  }

  function normEvent(e: any): UnifiedItem {
    const title = e.title || 'Event';
    const city = e.location?.city || e.location?.name || '';
    const sport = e.sport || '';
    const subtitle = [sport, city].filter(Boolean).join(' • ');
    const start = e.startDate ? String(e.startDate) : e.createdAt;
    const dateHint = e.startDate ? dayjs(e.startDate).format('MMM D, YYYY') : undefined;
    const img = e.image || e.cover || undefined;
    return {
      kind: 'event',
      id: e._id,
      title,
      subtitle,
      createdAt: start,
      dateHint,
      imageUrl: img,
      user: makeUser(e.organizer),
      raw: e,
    };
  }

  function normService(s: any): UnifiedItem {
    const title = s.name || s.title || 'Service';
    const city = s.location?.city || '';
    const cat = s.category || s.sport || '';
    const subtitle = [cat, city].filter(Boolean).join(' • ');
    const priceHint = s.pricing?.amount
      ? `${s.pricing.amount} ${s.pricing.currency || ''}`.trim()
      : undefined;
    const imageUrl = Array.isArray(s.images) && s.images.length > 0 ? s.images[0] : undefined;
    return {
      kind: 'service',
      id: s._id,
      title,
      subtitle,
      createdAt: s.createdAt,
      priceHint,
      imageUrl,
      user: makeUser(s.provider),
      raw: s,
    };
  }

  function normProduct(m: any): UnifiedItem {
    const title = m.title || 'Product';
    const subtitle = [m.category, m.location].filter(Boolean).join(' • ');
    const imageUrl = Array.isArray(m.images) && m.images.length > 0 ? m.images[0] : undefined;
    const priceHint = m.price !== undefined ? `${m.price} ${m.currency || ''}`.trim() : undefined;
    return {
      kind: 'product',
      id: m._id,
      title,
      subtitle,
      createdAt: m.createdAt,
      priceHint,
      imageUrl,
      user: makeUser(m.seller),
      raw: m,
    };
  }

  function normVenue(v: any): UnifiedItem {
    const title = v.name || 'Venue';
    const city = v.location?.city || v.location?.name || '';
    const capacity = v.capacity?.max ? `Capacity: ${v.capacity.max}` : '';
    const subtitle = [city, capacity].filter(Boolean).join(' • ');
    const imageUrl = Array.isArray(v.images) && v.images.length > 0 ? v.images[0] : undefined;
    return {
      kind: 'venue',
      id: v._id,
      title,
      subtitle,
      createdAt: v.createdAt,
      imageUrl,
      user: makeUser(v.owner),
      raw: v,
    };
  }

  async function loadEventFeed(initial = false) {
    try {
      setEventsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const nextEvent = initial ? 1 : eventPage + 1;
      const nextSvc = initial ? 1 : svcPage + 1;
      const nextMkt = initial ? 1 : mktPage + 1;
      const nextVenue = initial ? 1 : venuePage + 1;

      const [eventsRes, servicesRes, marketRes, venuesRes] = await Promise.all([
        axios.get(`${API}/api/events`, { headers, params: { page: initial ? 1 : nextEvent, limit: 10 } }),
        axios.get(`${API}/api/services`, {
          headers,
          params: { page: initial ? 1 : nextSvc, limit: 10 },
        }),
        axios.get(`${API}/api/marketplace`, {
          headers,
          params: { page: initial ? 1 : nextMkt, limit: 10 },
        }),
        axios.get(`${API}/api/venues/search`, {
          headers,
          params: { page: initial ? 1 : nextVenue, limit: 10, name: '' },
        }),
      ]);

      const evs = (eventsRes.data?.events || eventsRes.data || []).map(normEvent);
      const svs = (servicesRes.data?.services || servicesRes.data || []).map(normService);
      const mkt = (marketRes.data?.items || marketRes.data || []).map(normProduct);
      const vns = (venuesRes.data?.venues || venuesRes.data || []).map(normVenue);

      const combined = [...(initial ? [] : eventFeed), ...evs, ...svs, ...mkt, ...vns];
      const dedup = Array.from(new Map(combined.map((i) => [i.kind + ':' + i.id, i])).values());
      // Seed per refresh/load to rotate events ordering while keeping recency dominance
      const seed = (Date.now() ^ Math.floor(Math.random() * 0x7fffffff)) >>> 0;
      function scoreItem(x: UnifiedItem): number {
        const ta = x.createdAt ? dayjs(x.createdAt).valueOf() : 0;
        // Recency scaled like posts
        const ageHours = Math.max(0, (Date.now() - ta) / (1000 * 60 * 60));
        const recency = 1 / (1 + ageHours / 24);
        // Followed owner boost if present
        const uid = x.user?._id ? String(x.user._id) : '';
        const followed = uid && followingIds.has(uid) ? 1 : 0;
        // Kind-based jitter to interleave categories
        const kindNoise = seededNoise('kind:' + x.kind, seed);
        // Item-specific jitter
        const idNoise = seededNoise(x.kind + ':' + x.id, seed);
        return recency * 0.6 + followed * 0.25 + kindNoise * 0.1 + idNoise * 0.05;
      }
      const sorted =
        sortMode === 'newest'
          ? dedup.sort((a, b) => {
              const ta = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
              const tb = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
              return tb - ta;
            })
          : dedup.sort((a, b) => {
              const sa = scoreItem(a);
              const sb = scoreItem(b);
              if (sa !== sb) return sb - sa;
              const ta = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
              const tb = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
              if (ta !== tb) return tb - ta;
              return (
                seededNoise(a.kind + ':' + a.id, seed) - seededNoise(b.kind + ':' + b.id, seed)
              );
            });
      setEventFeed(sorted);
      const evDone = (eventsRes.data?.currentPage || 1) >= (eventsRes.data?.totalPages || 1);
      if (initial) {
        setEventPage(1);
        setSvcPage(1);
        setMktPage(1);
        setVenuePage(1);
        setHasMoreEvents(!(evDone));
      } else {
        setEventPage(nextEvent);
        setSvcPage(nextSvc);
        setMktPage(nextMkt);
        setVenuePage(nextVenue);
        const svcDone =
          servicesRes.data?.pagination?.page >= servicesRes.data?.pagination?.pages;
        const mktDone = marketRes.data?.currentPage >= marketRes.data?.totalPages;
        const vnsDone = (venuesRes.data?.page || 1) >= (venuesRes.data?.totalPages || 1);
        // Keep loading while any source still has more
        setHasMoreEvents(!(evDone && svcDone && mktDone && vnsDone));
      }
    } catch (e) {
      console.error('Failed to load events feed', e);
    } finally {
      setEventsLoading(false);
    }
  }

  async function loadServiceFeed(initial = false) {
    try {
      setServicesLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const nextSvc = initial ? 1 : servicePage + 1;
      const servicesRes = await axios.get(`${API}/api/services`, { headers, params: { page: nextSvc, limit: 10 } });
      const svs = (servicesRes.data?.services || servicesRes.data || []).map(normService);
      const combined = [...(initial ? [] : serviceFeed), ...svs];
      const dedup = Array.from(new Map(combined.map((i) => [i.kind + ':' + i.id, i])).values());
      setServiceFeed(dedup);
      const svcDone = servicesRes.data?.pagination?.page >= servicesRes.data?.pagination?.pages;
      if (initial) {
        setServicePage(1);
        setHasMoreServices(!svcDone);
      } else {
        setServicePage(nextSvc);
        setHasMoreServices(!svcDone);
      }
    } catch (e) {
      console.error('Failed to load services feed', e);
    } finally {
      setServicesLoading(false);
    }
  }

  async function loadProductFeed(initial = false) {
    try {
      setProductsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const nextMkt = initial ? 1 : productPage + 1;
      const marketRes = await axios.get(`${API}/api/marketplace`, { headers, params: { page: nextMkt, limit: 10 } });
      const mkt = (marketRes.data?.items || marketRes.data || []).map(normProduct);
      const combined = [...(initial ? [] : productFeed), ...mkt];
      const dedup = Array.from(new Map(combined.map((i) => [i.kind + ':' + i.id, i])).values());
      setProductFeed(dedup);
      const mktDone = marketRes.data?.currentPage >= marketRes.data?.totalPages;
      if (initial) {
        setProductPage(1);
        setHasMoreProducts(!mktDone);
      } else {
        setProductPage(nextMkt);
        setHasMoreProducts(!mktDone);
      }
    } catch (e) {
      console.error('Failed to load products feed', e);
    } finally {
      setProductsLoading(false);
    }
  }

  function TypePill({ kind }: { kind: UnifiedItem['kind'] }) {
    const cls =
      kind === 'event'
        ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
        : kind === 'service'
          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
          : kind === 'product'
            ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
            : 'bg-sky-500/10 text-sky-600 dark:text-sky-400';
    const Icon = kind === 'event' ? Calendar : kind === 'service' ? Briefcase : kind === 'product' ? ShoppingBag : MapPin;
    const label = kind === 'event' ? 'Event' : kind === 'service' ? 'Service' : kind === 'product' ? 'Product' : 'Venue';
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border ${cls}`}
        style={{ borderColor: 'var(--border)' }}
      >
        <Icon className="w-3.5 h-3.5" /> {label}
      </span>
    );
  }

  function makeAvatarUrl(avatar?: string) {
    if (!avatar) return 'https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff';
    if (avatar.startsWith('http')) return avatar;
    if (avatar.startsWith('/')) return API + avatar;
    return API + '/uploads/' + avatar;
  }

  // Safely extract a user id from various shapes (object or string)
  function getUserId(u: any): string | undefined {
    if (!u) return undefined;
    if (typeof u === 'string') return u;
    const id = u._id || u.id;
    return id ? String(id) : undefined;
  }

  async function handleCreatePost() {
    if (!newPost.caption.trim() && !newPost.imageUrl) {
      alert('Please add a caption or image');
      return;
    }
    if (containsOffensive(newPost.caption)) {
      alert('Please remove offensive words from your post caption.');
      return;
    }

    try {
      const tags = newPost.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const res = await axios.post(
        `${API}/api/posts`,
        {
          caption: newPost.caption,
          imageUrl: newPost.imageUrl,
          tags,
          location: newPost.location,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setPosts([res.data, ...posts]);
      setNewPost({ caption: '', imageUrl: '', location: '', tags: '' });
      setCreateModalOpen(false);
    } catch (err) {
      console.error('Failed to create post:', err);
      alert('Failed to create post');
    }
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be under 10MB');
      return;
    }

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/api/files/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setNewPost({ ...newPost, imageUrl: res.data.url });
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleLike(postId: string) {
    // Optimistic toggle
    const prev = posts;
    setPosts((list) =>
      list.map((p) => {
        if (String(p._id || p.id) !== String(postId)) return p;
        const likesArr = Array.isArray(p.likes) ? p.likes : [];
        const liked = likesArr.includes(currentUserId);
        return {
          ...p,
          likes: liked ? likesArr.filter((id) => id !== currentUserId) : [...likesArr, currentUserId],
        };
      }),
    );

    try {
      const res = await axios.post(
        `${API}/api/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPosts((list) => list.map((p) => (String(p._id || p.id) === String(postId) ? res.data : p)));
    } catch (err) {
      console.error('Failed to like post:', err);
      // Revert on error
      setPosts(prev);
    }
  }

  async function handleComment(postId: string) {
    const text = commentTexts[postId]?.trim();
    if (!text) return;

    // Optimistic add
    const prev = posts;
    const tempId = `temp-${Date.now()}`;
    const tempComment = {
      _id: tempId,
      user: {
        _id: currentUserId,
        username: currentUser?.username || 'You',
        avatar: currentUser?.avatar,
      },
      text,
      likes: [],
      replies: [],
      createdAt: new Date().toISOString(),
    } as any;

    setPosts((list) =>
      list.map((p) => (p._id === postId ? { ...p, comments: [...p.comments, tempComment] } : p)),
    );
    setCommentTexts({ ...commentTexts, [postId]: '' });

    try {
      const res = await axios.post(
        `${API}/api/posts/${postId}/comment`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPosts((list) => list.map((p) => (String(p._id || p.id) === String(postId) ? res.data : p)));
    } catch (err) {
      console.error('Failed to add comment:', err);
      // Revert and restore input
      setPosts(prev);
      setCommentTexts({ ...commentTexts, [postId]: text });
    }
  }

  async function handleDeletePost(postId: string) {
    if (!confirm('Delete this post?')) return;

    try {
      await axios.delete(`${API}/api/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPosts(posts.filter((p) => p._id !== postId));
    } catch (err) {
      console.error('Failed to delete post:', err);
      alert('Failed to delete post');
    }
  }

  function getPostShareUrl(post: Post) {
    try {
      const origin = window.location.origin;
      // Use a clean route for posts
      const url = `${origin}/posts/${post._id}`;
      return url;
    } catch {
      return `/posts/${post._id}`;
    }
  }

  function getUnifiedItemShareUrl(item: UnifiedItem) {
    try {
      const origin = window.location.origin;
      // Use a clean route for each item type
      let route = '';
      if (item.kind === 'event') route = `/events/${item.id}`;
      else if (item.kind === 'service') route = `/services/${item.id}`;
      else if (item.kind === 'product') route = `/products/${item.id}`;
      else if (item.kind === 'venue') route = `/venues/${item.id}`;
      else route = `/${item.kind}/${item.id}`;
      return origin + route;
    } catch {
      return `/${item.kind}/${item.id}`;
    }
  }

  async function handleSharePost(post: Post) {
    const shareUrl = getPostShareUrl(post);
    const title = `${post.author?.username || 'User'}'s post`;
    const text = post.caption || 'Check out this post on TeaKonn!';
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
        return;
      }
    } catch (e) {
      console.warn('Web Share aborted or failed:', e);
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard');
    } catch (e) {
      console.error('Clipboard copy failed:', e);
      alert(shareUrl);
    }
  }

  async function handleShareUnifiedItem(item: UnifiedItem) {
    const shareUrl = getUnifiedItemShareUrl(item);
    const title = `${item.title || (item.kind === 'event' ? 'Event' : item.kind === 'service' ? 'Service' : item.kind === 'product' ? 'Product' : 'Venue')}`;
    const text = `Check out this ${item.kind} on TeaKonn!`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
        return;
      }
    } catch (e) {
      console.warn('Web Share aborted or failed:', e);
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard');
    } catch (e) {
      console.error('Clipboard copy failed:', e);
      alert(shareUrl);
    }
  }

  function startEditPost(post: Post) {
    setEditingPostId(post._id);
    setEditPostData({
      caption: post.caption,
      location: post.location,
      tags: post.tags.join(', '),
    });
  }

  async function handleUpdatePost() {
    if (!editingPostId) return;

    try {
      const tags = editPostData.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const res = await axios.put(
        `${API}/api/posts/${editingPostId}`,
        {
          caption: editPostData.caption,
          tags,
          location: editPostData.location,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setPosts(posts.map((p) => (p._id === editingPostId ? res.data : p)));
      setEditingPostId(null);
      setEditPostData({ caption: '', location: '', tags: '' });
    } catch (err) {
      console.error('Failed to update post:', err);
      alert('Failed to update post');
    }
  }

  async function handleDeleteComment(postId: string, commentId: string) {
    if (!confirm('Delete this comment?')) return;

    try {
      const res = await axios.delete(`${API}/api/posts/${postId}/comment/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPosts(posts.map((p) => (String(p._id || p.id) === String(postId) ? res.data : p)));
    } catch (err) {
      console.error('Failed to delete comment:', err);
      alert('Failed to delete comment');
    }
  }

  function startEditComment(comment: any) {
    setEditingCommentId(comment._id);
    setEditCommentText(comment.text);
  }

  async function handleLikeComment(postId: string, commentId: string) {
    // Optimistic toggle
    const prev = posts;
    setPosts((list) =>
      list.map((p) => {
        if (p._id !== postId) return p;
        return {
          ...p,
          comments: p.comments.map((c) => {
            if (c._id !== commentId) return c;
            const likes = c.likes || [];
            const liked = likes.includes(currentUserId);
            return {
              ...c,
              likes: liked ? likes.filter((id) => id !== currentUserId) : [...likes, currentUserId],
            };
          }),
        };
      }),
    );

    try {
      const res = await axios.post(
        `${API}/api/posts/${postId}/comment/${commentId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPosts((list) => list.map((p) => (String(p._id || p.id) === String(postId) ? res.data : p)));
    } catch (err) {
      console.error('Failed to like comment:', err);
      setPosts(prev);
    }
  }

  async function handleReplyToComment(postId: string, commentId: string) {
    if (!replyText.trim()) return;

    const text = replyText.trim();
    const prev = posts;
    const tempReply = {
      user: {
        _id: currentUserId,
        username: currentUser?.username || 'You',
        avatar: currentUser?.avatar,
      },
      text,
      createdAt: new Date().toISOString(),
      _id: `tempreply-${Date.now()}`,
    } as any;

    // Optimistic add
    setPosts((list) =>
      list.map((p) => {
        if (p._id !== postId) return p;
        return {
          ...p,
          comments: p.comments.map((c) =>
            c._id === commentId ? { ...c, replies: [...(c.replies || []), tempReply] } : c,
          ),
        };
      }),
    );
    setReplyingTo(null);
    setReplyText('');

    try {
      const res = await axios.post(
        `${API}/api/posts/${postId}/comment/${commentId}/reply`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPosts((list) => list.map((p) => (String(p._id || p.id) === String(postId) ? res.data : p)));
    } catch (err) {
      console.error('Failed to reply to comment:', err);
      alert('Failed to reply to comment');
      setPosts(prev);
      // Optionally restore reply draft
      setReplyingTo(commentId);
      setReplyText(text);
    }
  }

  function startEditReply(commentId: string, reply: any) {
    const text = reply.text || '';
    setEditingReply({ commentId, replyId: String(reply._id || ''), text });
  }

  async function handleSaveReply(postId: string) {
    if (!editingReply) return;
    const { commentId, replyId, text } = editingReply;
    if (!text.trim()) return;

    const prev = posts;
    // Optimistic update
    setPosts((list) =>
      list.map((p) => {
        if (p._id !== postId) return p;
        return {
          ...p,
          comments: p.comments.map((c) => {
            if (c._id !== commentId) return c;
            return {
              ...c,
              replies: (c.replies || []).map((r: any) =>
                String(r._id) === String(replyId) ? { ...r, text } : r,
              ),
            };
          }),
        };
      }),
    );

    try {
      const res = await axios.put(
        `${API}/api/posts/${postId}/comment/${commentId}/reply/${replyId}`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPosts((list) => list.map((p) => (String(p._id || p.id) === String(postId) ? res.data : p)));
      setEditingReply(null);
    } catch (err) {
      console.error('Failed to edit reply:', err);
      alert('Failed to edit reply');
      setPosts(prev);
    }
  }

  async function handleDeleteReply(postId: string, commentId: string, replyId: string) {
    if (!confirm('Delete this reply?')) return;

    const prev = posts;
    // Optimistic remove
    setPosts((list) =>
      list.map((p) => {
        if (p._id !== postId) return p;
        return {
          ...p,
          comments: p.comments.map((c) =>
            c._id === commentId
              ? { ...c, replies: (c.replies || []).filter((r: any) => String(r._id) !== String(replyId)) }
              : c,
          ),
        };
      }),
    );

    try {
      const res = await axios.delete(
        `${API}/api/posts/${postId}/comment/${commentId}/reply/${replyId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPosts((list) => list.map((p) => (String(p._id || p.id) === String(postId) ? res.data : p)));
    } catch (err) {
      console.error('Failed to delete reply:', err);
      alert('Failed to delete reply');
      setPosts(prev);
    }
  }

  function toggleComments(postId: string) {
    setExpandedComments((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  }

  function toggleShowAll(postId: string) {
    setShowAllComments((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  }

  function toggleReplies(commentId: string) {
    setExpandedReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  }

  function formatTimestamp(dateString: string) {
    const date = dayjs(dateString);
    const now = dayjs();
    const diffInHours = now.diff(date, 'hour');

    if (diffInHours < 24) {
      return date.fromNow(); // "2 hours ago"
    } else if (diffInHours < 168) {
      // Less than 7 days
      return date.format('dddd [at] h:mm A'); // "Monday at 3:45 PM"
    } else {
      return date.format('MMM D, YYYY [at] h:mm A'); // "Dec 14, 2025 at 3:45 PM"
    }
  }

  function toggleCommentBox(postId: string) {
    setCommentBoxOpen((prev) => ({ ...prev, [postId]: !prev[postId] }));
  }

  function renderPostsTab() {
    if (loading) {
      return (
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl p-6 animate-pulse themed-card">
              <div className="h-64 themed-card"></div>
            </div>
          ))}
        </div>
      );
    }
    if (posts.length === 0) {
      return (
        <div className="text-center py-20">
          <ImageIcon className="w-16 h-16 text-theme-secondary mx-auto mb-4" />
          <p className="text-theme-secondary text-lg">No posts yet</p>
          <p className="text-theme-secondary text-sm mt-2">Be the first to share something!</p>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {/* Hint removed: using explicit actions menu button now */}
        {visiblePosts.map((post) => {
          const postLikes = Array.isArray(post.likes) ? post.likes : [];
          const likeCount =
            typeof post.likes_count === 'number' ? post.likes_count : postLikes.length;
          const postComments = Array.isArray(post.comments) ? post.comments : [];
          const postTags = Array.isArray(post.tags) ? post.tags : [];
          const postKey = String(post._id || post.id || '');

          return (
            <div
              key={postKey}
              id={`post-${postKey}`}
              className="rounded-xl themed-card overflow-hidden"
              style={{ border: 'none' }}
            >
            {/* Post Header (shown only when no image) */}
            {!post.imageUrl && (
              <div className="flex items-center justify-between p-2 relative z-10">
                <div className="flex items-center gap-3">
                  <Avatar
                    src={makeAvatarUrl(post.author?.avatar)}
                    className="w-10 h-10 rounded-full object-cover cursor-pointer"
                    alt={post.author?.username || 'User'}
                    onClick={() => post.author && onShowProfile(post.author)}
                  />
                  <div>
                    <div
                      className="font-semibold text-heading cursor-pointer hover:text-cyan-500"
                      onClick={() => post.author && onShowProfile(post.author)}
                    >
                      {post.author?.username || 'User'}
                    </div>
                    {post.location && (
                      <div className="text-xs text-theme-secondary">{formatLocationText(post.location)}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenPostMenuId((id) => (id === post._id ? null : post._id));
                    }}
                    className="hover:opacity-80"
                    title="More"
                    aria-haspopup="menu"
                    aria-expanded={openPostMenuId === post._id}
                  >
                    <MoreVertical className="w-5 h-5 text-theme-secondary" />
                  </button>
                  {openPostMenuId === post._id && (
                    <div
                      className="absolute right-0 top-8 z-20 w-60 rounded-xl shadow-xl border"
                      style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {post.author._id === currentUserId && (
                        <>
                          <button
                            onClick={() => {
                              setOpenPostMenuId(null);
                              startEditPost(post);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-black/5 dark:hover:bg-white/10"
                          >
                            <Edit className="w-4 h-4" />
                            Edit Post
                          </button>
                          <button
                            onClick={() => {
                              setOpenPostMenuId(null);
                              handleDeletePost(post._id);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-black/5 dark:hover:bg-white/10"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Post
                          </button>
                          <div className="border-t" style={{ borderColor: 'var(--border)' }} />
                        </>
                      )}
                      <button
                        onClick={() => {
                          setOpenPostMenuId(null);
                          handleSharePost(post);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-black/5 dark:hover:bg-white/10"
                      >
                        <Share className="w-4 h-4" />
                        Share Post
                      </button>
                      <div className="border-t" style={{ borderColor: 'var(--border)' }} />
                      <button
                        onClick={() => setOpenPostMenuId(null)}
                        className="w-full px-4 py-2.5 text-xs text-theme-secondary text-left hover:bg-black/5 dark:hover:bg-white/10"
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Post Image (overlay header/actions + caption for photo-centric look) */}
            {post.imageUrl && (
              <div
                className="w-full h-64 sm:h-80 md:h-[24rem] overflow-hidden relative group cursor-zoom-in"
                onClick={() => openImage(post.imageUrl)}
              >
                <img src={post.imageUrl} alt="Post" className="w-full h-full object-cover" />
                {/* Top gradient for readability */}
                <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/60 via-black/20 to-transparent" />
                {/* Bottom gradient for caption */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                {/* Overlay header and actions */}
                <div className="absolute top-2 left-2 right-2 flex items-center justify-between pointer-events-none">
                  <div className="flex items-center gap-2 pointer-events-auto">
                    <Avatar
                      src={makeAvatarUrl(post.author?.avatar)}
                      className="w-8 h-8 rounded-full object-cover cursor-pointer"
                      alt={post.author?.username || 'User'}
                      onClick={(e) => { e.stopPropagation(); post.author && onShowProfile(post.author); }}
                    />
                    <div className="text-white drop-shadow">
                      <div className="font-semibold cursor-pointer hover:opacity-90" onClick={(e) => { e.stopPropagation(); post.author && onShowProfile(post.author); }}>
                        {post.author?.username || 'User'}
                      </div>
                      {post.location && (
                        <div className="text-xs opacity-90">{formatLocationText(post.location)}</div>
                      )}
                    </div>
                  </div>
                  <div className="pointer-events-auto relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenPostMenuId((id) => (id === post._id ? null : post._id)); }}
                      className="text-white hover:opacity-80"
                      title="More"
                      aria-haspopup="menu"
                      aria-expanded={openPostMenuId === post._id}
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    {openPostMenuId === post._id && (
                      <>
                        {/* Overlay to capture outside clicks (mobile shows dimmed backdrop) */}
                        <div
                          className="fixed inset-0 z-40 bg-black/20 sm:bg-transparent"
                          onClick={(e) => { e.stopPropagation(); setOpenPostMenuId(null); }}
                          aria-hidden="true"
                        />
                        {/* Responsive options menu: bottom sheet on mobile, popover on desktop */}
                        <div
                          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[92vw] max-w-sm rounded-2xl shadow-2xl border p-2 sm:absolute sm:bottom-auto sm:left-auto sm:right-0 sm:top-8 sm:w-64 sm:rounded-xl sm:shadow-xl"
                          style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                          onClick={(e) => e.stopPropagation()}
                          role="menu"
                          aria-label="Post options"
                        >
                          <div className="flex items-center justify-between px-2 pb-2 sm:hidden">
                            <div className="text-xs text-theme-secondary">Post Options</div>
                            <button
                              onClick={() => setOpenPostMenuId(null)}
                              className="themed-card rounded-full p-1"
                              aria-label="Close options"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {post.author._id === currentUserId && (
                            <>
                              <button
                                onClick={() => { setOpenPostMenuId(null); startEditPost(post); }}
                                className="flex items-center gap-2 w-full px-4 py-3 text-sm hover:bg-black/5 dark:hover:bg-white/10 rounded-lg"
                                role="menuitem"
                              >
                                <Edit className="w-4 h-4" /> Edit Post
                              </button>
                              <button
                                onClick={() => { setOpenPostMenuId(null); handleDeletePost(post._id); }}
                                className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg"
                                role="menuitem"
                              >
                                <Trash2 className="w-4 h-4" /> Delete Post
                              </button>
                              <div className="border-t my-1" style={{ borderColor: 'var(--border)' }} />
                            </>
                          )}
                          <button
                            onClick={() => { setOpenPostMenuId(null); handleSharePost(post); }}
                            className="flex items-center gap-2 w-full px-4 py-3 text-sm hover:bg-black/5 dark:hover:bg-white/10 rounded-lg"
                            role="menuitem"
                          >
                            <Share className="w-4 h-4" /> Share Post
                          </button>
                          <div className="mt-2 flex sm:hidden">
                            <button
                              onClick={() => setOpenPostMenuId(null)}
                              className="w-full px-4 py-2 themed-card rounded-lg"
                              aria-label="Close"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Caption overlay removed to avoid duplicate caption; caption renders below image */}
              </div>
            )}

            {/* Actions */}
            <div className="p-3 space-y-3">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleLike(String(post._id || post.id))}
                  className="flex items-center gap-1.5 group"
                >
                  <Heart
                    className={`w-5 h-5 transition-all ${
                      postLikes.includes(currentUserId)
                        ? 'fill-red-500 text-red-500'
                        : 'text-theme-secondary group-hover:text-red-500'
                    }`}
                  />
                  <span className="text-sm font-medium text-theme-secondary">
                    {likeCount}
                  </span>
                </button>
                <button
                  onClick={() => toggleCommentBox(String(post._id || post.id))}
                  className="flex items-center gap-1.5 text-theme-secondary hover:text-cyan-500"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">{postComments.length}</span>
                </button>
              </div>

              {/* Caption - Editable if editing */}
              {editingPostId === post._id ? (
                <div className="space-y-3 p-4 themed-card rounded-lg">
                  <textarea
                    value={editPostData.caption}
                    onChange={(e) =>
                      setEditPostData({ ...editPostData, caption: e.target.value })
                    }
                    className="input w-full resize-none"
                    rows={3}
                    placeholder="Caption..."
                    maxLength={280}
                  />
                  <input
                    type="text"
                    value={editPostData.location}
                    onChange={(e) =>
                      setEditPostData({ ...editPostData, location: e.target.value })
                    }
                    className="input w-full"
                    placeholder="Location..."
                    maxLength={100}
                  />
                  <input
                    type="text"
                    value={editPostData.tags}
                    onChange={(e) => setEditPostData({ ...editPostData, tags: e.target.value })}
                    className="input w-full"
                    placeholder="Tags (comma-separated)..."
                    maxLength={100}
                  />
                  <div className="flex gap-2">
                    <button onClick={handleUpdatePost} className="btn">
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingPostId(null);
                        setEditPostData({ caption: '', location: '', tags: '' });
                      }}
                      className="themed-card"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {post.caption && (
                    <div>
                      <p
                        className="text-heading"
                        style={
                          expandedCaptions[post._id]
                            ? {}
                            : {
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }
                        }
                      >
                        <span className="font-semibold mr-2">{post.author?.username || 'User'}</span>
                        {post.caption}
                      </p>
                      {post.caption.length > 240 && (
                        <button
                          className="mt-1 text-xs text-cyan-600 dark:text-cyan-400 hover:opacity-80"
                          onClick={() =>
                            setExpandedCaptions((prev) => ({
                              ...prev,
                              [post._id]: !prev[post._id],
                            }))
                          }
                        >
                          {expandedCaptions[post._id] ? 'See less' : 'See more'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Tags */}
                  {postTags.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      {postTags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 rounded-full text-xs text-cyan-600 dark:text-cyan-400"
                          style={{ border: '1px solid var(--border)' }}
                        >
                          #{tag}
                        </span>
                      ))}
                      {postTags.length > 3 && (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs text-theme-secondary"
                          style={{ border: '1px solid var(--border)' }}
                        >
                          …
                        </span>
                      )}
                    </div>
                  )}

                  {/* Timestamp */}
                  <div className="text-xs text-theme-secondary">
                    {formatTimestamp(post.createdAt)}
                    {post.captionEditedAt && <span className="ml-2 italic">(edited)</span>}
                  </div>
                </>
              )}

              {/* Comments */}
              {postComments.length > 0 && (
                <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                  {!expandedComments[post._id] ? (
                    <button
                      onClick={() => toggleComments(post._id)}
                      className="text-sm text-theme-secondary hover:opacity-80 mb-2"
                    >
                      Show comments ({postComments.length})
                    </button>
                  ) : (
                    <>
                      {postComments.length > 3 && !showAllComments[post._id] && (
                        <button
                          onClick={() => toggleShowAll(post._id)}
                          className="text-sm text-theme-secondary hover:opacity-80 mb-2"
                        >
                          View all {postComments.length} comments
                        </button>
                      )}
                      <div className="space-y-3">
                        {(showAllComments[post._id]
                          ? postComments
                          : postComments.slice(-3)
                        ).map((comment) => (
                          <div key={comment._id} className="flex gap-2 group">
                            <Avatar
                              src={makeAvatarUrl(comment.user?.avatar)}
                              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                              alt={comment.user?.username || 'User'}
                            />
                            <div className="flex-1 min-w-0">
                              {editingCommentId === comment._id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editCommentText}
                                    onChange={(e) => setEditCommentText(e.target.value)}
                                    className="input w-full text-sm resize-none"
                                    rows={2}
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={async () => {
                                        if (!editCommentText.trim()) return;
                                        try {
                                          const res = await axios.put(
                                            `${API}/api/posts/${post._id}/comment/${comment._id}`,
                                            { text: editCommentText },
                                            { headers: { Authorization: `Bearer ${token}` } },
                                          );
                                          setPosts(
                                            posts.map((p) =>
                                              p._id === post._id ? res.data : p,
                                            ),
                                          );
                                          setEditingCommentId(null);
                                          setEditCommentText('');
                                        } catch (err) {
                                          console.error('Failed to edit comment:', err);
                                          alert('Failed to edit comment');
                                        }
                                      }}
                                      className="btn text-xs"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingCommentId(null)}
                                      className="themed-card text-xs"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <span className="font-semibold text-sm text-heading mr-2">
                                        {comment.user.username}
                                      </span>
                                      <div className="text-sm text-theme-secondary">
                                        <div
                                          className="break-words whitespace-pre-wrap hyphens-auto max-w-full"
                                          style={{
                                            display: expandedCommentText[comment._id]
                                              ? 'block'
                                              : '-webkit-box',
                                            WebkitLineClamp: expandedCommentText[comment._id]
                                              ? undefined
                                              : 4,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: expandedCommentText[comment._id]
                                              ? 'visible'
                                              : 'hidden',
                                          }}
                                        >
                                          {comment.text}
                                        </div>
                                        {comment.text && comment.text.length > 200 && (
                                          <button
                                            className="mt-1 text-xs text-cyan-600 dark:text-cyan-400 hover:opacity-80"
                                            onClick={() =>
                                              setExpandedCommentText((prev) => ({
                                                ...prev,
                                                [comment._id]: !prev[comment._id],
                                              }))
                                            }
                                          >
                                            {expandedCommentText[comment._id]
                                              ? 'See less'
                                              : 'See more'}
                                          </button>
                                        )}
                                      </div>
                                      <div className="flex items-center flex-wrap gap-3 mt-1 min-w-0">
                                        <span className="text-xs text-theme-secondary">
                                          {formatTimestamp(comment.createdAt)}
                                        </span>
                                        {comment.likes && comment.likes.length > 0 && (
                                          <span className="text-xs text-theme-secondary">
                                            {comment.likes.length}{' '}
                                            {comment.likes.length === 1 ? 'like' : 'likes'}
                                          </span>
                                        )}
                                        <button
                                          onClick={() =>
                                            handleLikeComment(post._id, comment._id)
                                          }
                                          className={`text-xs font-medium ${
                                            comment.likes?.includes(currentUserId)
                                              ? 'text-red-500'
                                              : 'text-theme-secondary hover:text-red-500'
                                          }`}
                                        >
                                          Like
                                        </button>
                                        <button
                                          onClick={() => {
                                            setReplyingTo(comment._id);
                                            setReplyText('');
                                          }}
                                          className="text-xs text-theme-secondary hover:text-cyan-500 font-medium"
                                        >
                                          Reply
                                        </button>
                                        {getUserId(comment.user) === currentUserId && (
                                          <>
                                            <button
                                              onClick={() => startEditComment(comment)}
                                              className="text-xs text-gray-500 dark:text-gray-400 hover:text-cyan-500 font-medium"
                                            >
                                              Edit
                                            </button>
                                          </>
                                        )}
                                        {post.author._id === currentUserId &&
                                          getUserId(comment.user) !== currentUserId && (
                                            <button
                                              onClick={() =>
                                                handleDeleteComment(post._id, comment._id)
                                              }
                                              className="text-xs text-red-500 hover:text-red-600 font-medium"
                                            >
                                              Delete
                                            </button>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}

                              {comment.replies && comment.replies.length > 0 && (
                                <div
                                  className="ml-4 mt-2 border-l-2 pl-3 max-w-full overflow-x-hidden"
                                  style={{ borderColor: 'var(--border)' }}
                                >
                                  {!expandedReplies[comment._id] ? (
                                    <button
                                      onClick={() => toggleReplies(comment._id)}
                                      className="text-xs text-theme-secondary hover:opacity-80"
                                    >
                                      View replies ({comment.replies.length})
                                    </button>
                                  ) : (
                                    <>
                                      <div className="space-y-2">
                                        {comment.replies.map((reply: any, idx: number) => {
                                          const ru = getReplyUser(reply.user);
                                          const displayName = ru.username || 'User';
                                          const displayAvatar = makeAvatarUrl(ru.avatar);
                                          return (
                                            <div key={idx} className="flex gap-2">
                                              <Avatar
                                                src={displayAvatar}
                                                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                                                alt={displayName}
                                              />
                                              <div className="min-w-0 max-w-full">
                                                <span className="font-semibold text-xs text-heading mr-1">
                                                  {displayName}
                                                </span>
                                              {editingReply?.replyId === String(reply._id) &&
                                              editingReply?.commentId === comment._id ? (
                                                <div className="mt-1 flex flex-col sm:flex-row gap-2 w-full">
                                                  <input
                                                    type="text"
                                                    className="input w-full sm:flex-1 min-w-0 text-xs"
                                                    value={editingReply.text}
                                                    onChange={(e) =>
                                                      setEditingReply({
                                                        ...editingReply,
                                                        text: e.target.value,
                                                      })
                                                    }
                                                    maxLength={REPLY_MAX}
                                                  />
                                                  <div className="text-[10px] text-theme-secondary self-end sm:self-center">
                                                    {editingReply.text.trim().length}/{REPLY_MAX}
                                                  </div>
                                                  <div className="flex gap-2">
                                                    <button
                                                      className="btn px-2.5 py-1.5 text-xs"
                                                      onClick={() => handleSaveReply(post._id)}
                                                    >
                                                      <Send className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                      className="themed-card px-2.5 py-1.5 text-xs"
                                                      onClick={() => setEditingReply(null)}
                                                    >
                                                      <X className="w-4 h-4" />
                                                    </button>
                                                  </div>
                                                </div>
                                              ) : (
                                                <>
                                                  <div className="text-xs text-theme-secondary">
                                                    <div
                                                      className="break-words whitespace-pre-wrap hyphens-auto max-w-full"
                                                      style={{
                                                        display: expandedReplyText[comment._id]?.[idx]
                                                          ? 'block'
                                                          : '-webkit-box',
                                                        WebkitLineClamp: expandedReplyText[comment._id]?.[idx]
                                                          ? undefined
                                                          : 3,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: expandedReplyText[comment._id]?.[idx]
                                                          ? 'visible'
                                                          : 'hidden',
                                                      }}
                                                    >
                                                      {reply.text}
                                                    </div>
                                                    <div className="flex items-center flex-wrap gap-3 mt-0.5 min-w-0">
                                                      <span className="text-xs text-theme-secondary">
                                                        {formatTimestamp(reply.createdAt)}
                                                      </span>
                                                      <button
                                                        className="text-xs text-theme-secondary hover:text-cyan-500 font-medium"
                                                        onClick={() => {
                                                          setReplyingTo(comment._id);
                                                          setReplyText(`@${displayName} `);
                                                          setReplyingToUser(displayName);
                                                        }}
                                                      >
                                                        Reply
                                                      </button>
                                                      {getUserId(reply.user) === currentUserId && (
                                                        <button
                                                          className="text-xs text-gray-500 dark:text-gray-400 hover:text-cyan-500 font-medium"
                                                          onClick={() => startEditReply(comment._id, reply)}
                                                        >
                                                          Edit
                                                        </button>
                                                      )}
                                                      {(post.author._id === currentUserId || getUserId(reply.user) === currentUserId) && (
                                                        <button
                                                          className="text-xs text-red-500 hover:text-red-600 font-medium"
                                                          onClick={() =>
                                                            handleDeleteReply(post._id, comment._id, String(reply._id))
                                                          }
                                                        >
                                                          Delete
                                                        </button>
                                                      )}
                                                    </div>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        );
                                        })}
                                      </div>
                                      <button
                                        onClick={() => toggleReplies(comment._id)}
                                        className="text-xs text-theme-secondary hover:opacity-80 mt-1"
                                      >
                                        Hide replies
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}

                              {replyingTo === comment._id && (
                                <div className="ml-4 mt-2 flex flex-col sm:flex-row items-stretch gap-2 w-full max-w-full">
                                  <input
                                    type="text"
                                    placeholder="Write a reply..."
                                    className="input w-full sm:flex-1 min-w-0 text-xs"
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleReplyToComment(post._id, comment._id);
                                      }
                                    }}
                                    maxLength={REPLY_MAX}
                                    autoFocus
                                  />
                                  <div className="text-[10px] text-theme-secondary self-end sm:self-center">
                                    {replyText.trim().length}/{REPLY_MAX}
                                  </div>
                                  <div className="flex sm:flex-row flex-row gap-2 sm:items-center w-full sm:w-auto">
                                    <button
                                      onClick={() => handleReplyToComment(post._id, comment._id)}
                                      className="btn px-2.5 py-1.5 text-xs shrink-0"
                                      aria-label="Send reply"
                                      title="Send"
                                    >
                                      <Send className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setReplyingTo(null);
                                        setReplyText('');
                                      }}
                                      className="themed-card px-2.5 py-1.5 text-xs shrink-0"
                                      aria-label="Cancel reply"
                                      title="Cancel"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        {postComments.length > 3 && showAllComments[post._id] && (
                          <button
                            onClick={() => toggleShowAll(post._id)}
                            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            Show recent
                          </button>
                        )}
                        <button
                          onClick={() => toggleComments(post._id)}
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          Hide comments
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {commentBoxOpen[post._id] && (
                <div
                  className="flex items-center gap-2 pt-3 mt-1 border-t rounded-xl"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <input
                    type="text"
                    placeholder="Add a comment..."
                    className="input flex-1 min-w-0 text-sm"
                    value={commentTexts[post._id] || ''}
                    onChange={(e) =>
                      setCommentTexts({ ...commentTexts, [post._id]: e.target.value })
                    }
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleComment(post._id);
                    }}
                    maxLength={300}
                    autoFocus
                  />
                  <button
                    onClick={() => handleComment(post._id)}
                    className="btn px-3 py-2 shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
        })}
        {hasMorePosts && (
          <div className="flex justify-center pt-2">
            <button
              className="px-4 py-2 rounded-xl border text-sm"
              style={{ borderColor: 'var(--border)' }}
              onClick={() => loadPosts(false)}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen themed-page p-4 sm:p-6">
      <div className="max-w-3xl lg:max-w-4xl mx-auto">
        {/* Header + Tabs */}
        <div className="mb-2">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              Feed
            </h1>
          </div>
          <div className="mt-1 -mx-4 sm:-mx-6">
            <div className="p-[1px] bg-gradient-to-r from-cyan-400 to-purple-500 rounded-none sm:rounded-xl">
              <div
                className="flex items-center gap-2 themed-card rounded-none sm:rounded-xl px-2 py-1 w-full flex-nowrap"
                style={{ background: 'var(--card)' }}
              >
                {/* Single cycling sort toggle to save space on mobile */}
                <button
                  className={`px-2 py-0.5 text-[12px] font-semibold leading-tight rounded-md flex-none border transition-colors ${
                    sortMode === 'prioritized'
                      ? 'bg-cyan-600 text-white border-cyan-500'
                      : sortMode === 'newest'
                        ? 'bg-purple-600 text-white border-purple-500'
                        : 'bg-slate-700 text-white border-slate-600'
                  }`}
                  onClick={() => setSortMode((m) => (m === 'prioritized' ? 'newest' : m === 'newest' ? 'random' : 'prioritized'))}
                  aria-label="Toggle sort"
                  aria-live="polite"
                >
                  {sortMode === 'prioritized' ? 'Prioritized' : sortMode === 'newest' ? 'Newest' : 'Random'}
                </button>
                {/* Near Me toggle */}
                <button
                  className={`px-2 py-0.5 text-[12px] font-semibold leading-tight rounded-md flex-none border transition-colors ${nearMeEnabled ? 'bg-indigo-600 text-white border-indigo-600' : ''}`}
                  style={{ borderColor: 'var(--border)' }}
                  onClick={async () => {
                    if (nearMeEnabled) {
                      setNearMeEnabled(false);
                      setUserLoc(null);
                      setApproxLoc(false);
                      setGeoError(null);
                      return;
                    }
                    try {
                      setLocating(true);
                      // Try precise location first
                      const pos = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
                      setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                      setApproxLoc(false);
                      setNearMeEnabled(true);
                    } catch (e) {
                      console.warn('Geolocation error', e);
                      // Fallback to approximate IP-based location
                      const approx = await getApproximateLocation();
                      if (approx) {
                        setUserLoc(approx);
                        setApproxLoc(true);
                        setNearMeEnabled(true);
                        setGeoError('Using approximate location (IP-based).');
                      } else {
                        setGeoError(deriveGeoErrorMessage(e, permissionState));
                      }
                    } finally {
                      setLocating(false);
                    }
                  }}
                  aria-pressed={nearMeEnabled}
                  aria-label="Toggle near me"
                  title={nearMeEnabled ? 'Near Me: Enabled' : 'Use My Location'}
                >
                  <MapPin className="w-3.5 h-3.5" /> {nearMeEnabled ? (locating ? 'Locating...' : `${approxLoc ? 'Near Me (approx) - ' : 'Near Me - '}${nearRadiusKm} km`) : 'Location'}
                </button>
              </div>
            </div>
          </div>
          {/* Tab Content */}
          {/* Toggle + Search Row */}
          <div className="mt-3 mb-3">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center">
              {/* Segmented toggle */}
              <div className="inline-flex rounded-md overflow-hidden border flex-none" style={{ borderColor: 'var(--border)' }} role="tablist" aria-label="Feed tabs">
                <button
                  className={`px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${tab === 'posts' ? 'bg-cyan-600 text-white' : 'bg-transparent text-theme'} border-r`}
                  style={{ borderColor: 'var(--border)' }}
                  onClick={() => setTab('posts')}
                  role="tab"
                  aria-selected={tab === 'posts'}
                >
                  Posts
                </button>
                <button
                  className={`px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${tab === 'events' ? 'bg-purple-600 text-white' : 'bg-transparent text-theme'}`}
                  onClick={() => setTab('events')}
                  role="tab"
                  aria-selected={tab === 'events'}
                >
                  Events
                </button>
                <button
                  className={`px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${tab === 'services' ? 'bg-indigo-600 text-white' : 'bg-transparent text-theme'} border-l`}
                  style={{ borderColor: 'var(--border)' }}
                  onClick={() => setTab('services')}
                  role="tab"
                  aria-selected={tab === 'services'}
                >
                  Services
                </button>
                <button
                  className={`px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${tab === 'products' ? 'bg-pink-600 text-white' : 'bg-transparent text-theme'} border-l`}
                  style={{ borderColor: 'var(--border)' }}
                  onClick={() => setTab('products')}
                  role="tab"
                  aria-selected={tab === 'products'}
                >
                  Products
                </button>
              </div>

              {/* Search with plus */}
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={tab === 'posts' ? 'Search posts...' : tab === 'events' ? 'Search events & venues...' : tab === 'services' ? 'Search services...' : 'Search products...'}
                    className="input w-full pr-10 shadow-sm focus:shadow-md focus:outline-none"
                    aria-label="Search feed"
                  />
                  <button
                    onClick={() => {
                      if (tab === 'posts') setCreateModalOpen(true);
                      else if (tab === 'events') setShowCreateEvent(true);
                      else if (tab === 'services') {
                        try { localStorage.setItem('auralink-create-service', '1'); } catch {}
                        onNavigate && onNavigate('dashboard');
                      } else {
                        try { localStorage.setItem('auralink-create-product', '1'); } catch {}
                        onNavigate && onNavigate('dashboard');
                      }
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1 rounded-md hover:opacity-80"
                    aria-label={tab === 'posts' ? 'Add post' : 'Add event'}
                    title={tab === 'posts' ? 'Add Post' : 'Add Event'}
                    style={{ background: 'var(--card)' }}
                  >
                    <Plus className="w-4 h-4 text-theme" />
                  </button>
                </div>
                </div>
              </div>
              {/* Near Me radius selector (visible when enabled) */}
              {nearMeEnabled && (
                <div className="flex items-center gap-1 flex-none">
                  <label className="text-xs text-theme-secondary">Radius</label>
                  <select
                    className="input px-2 py-1 text-xs w-[80px]"
                    value={nearRadiusKm}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setNearRadiusKm(v);
                      try { localStorage.setItem('auralink-near-radius-km', String(v)); } catch {}
                    }}
                  >
                    <option value={5}>5 km</option>
                    <option value={10}>10 km</option>
                    <option value={25}>25 km</option>
                    <option value={50}>50 km</option>
                  </select>
                  {geoError && (
                    <span className="ml-2 text-[11px] text-rose-500">{geoError}</span>
                  )}
                </div>
              )}
            </div>

            {/* Actual Tab Content */}
            {tab === 'posts' ? renderPostsTab() : renderEventsTab()}
          </div>
        </div>

        {/* Lightbox Preview */}
        {previewImage && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closeImage}
          >
            <button
              onClick={closeImage}
              className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
              aria-label="Close image preview"
            >
              <X className="w-6 h-6" />
            </button>
            {imageList.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 mid:top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 rounded-full p-3"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 mid:top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 rounded-full p-3"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  shareImage(previewImage);
                }}
                className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm"
              >
                Share
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadImage(previewImage);
                }}
                className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm"
              >
                Download
              </button>
            </div>
          </div>
        )}
      {/* Create Post Modal */}
      {/* Create Post Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div
            className="rounded-2xl p-6 w-full max-w-lg"
            style={{
              background: 'var(--card)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-heading">Create Post</h2>
              <button
                onClick={() => setCreateModalOpen(false)}
                className="p-2 hover:opacity-80 rounded-full themed-card"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <textarea
                placeholder="What's on your mind?"
                className="input w-full resize-none"
                rows={4}
                value={newPost.caption}
                onChange={(e) => setNewPost({ ...newPost, caption: e.target.value })}
                maxLength={280}
              />

              <input
                type="text"
                placeholder="Location (optional)"
                className="input w-full"
                value={newPost.location}
                onChange={(e) => setNewPost({ ...newPost, location: e.target.value })}
                maxLength={100}
              />

              <input
                type="text"
                placeholder="Tags (comma-separated)"
                className="input w-full"
                value={newPost.tags}
                onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
                maxLength={100}
              />

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-2">Image</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="post-image-upload"
                />
                <label
                  htmlFor="post-image-upload"
                  className="flex items-center justify-center gap-2 w-full px-4 py-8 rounded-xl cursor-pointer hover:border-cyan-400 transition-colors"
                  style={{ border: '2px dashed var(--border)' }}
                >
                  {uploadingImage ? (
                    <span className="text-theme-secondary">Uploading...</span>
                  ) : newPost.imageUrl ? (
                    <img src={newPost.imageUrl} alt="Preview" className="max-h-40 rounded-lg" />
                  ) : (
                    <>
                      <ImageIcon className="w-6 h-6 text-theme-secondary" />
                      <span className="text-theme-secondary">Click to upload image</span>
                    </>
                  )}
                </label>
              </div>

              <button
                onClick={handleCreatePost}
                disabled={uploadingImage}
                className="btn w-full py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingImage ? 'Uploading...' : 'Post'}
              </button>
              </div>
            </div>
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 p-4 bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all z-50 hover:scale-110"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}

      {/* Detail Modals */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onJoin={async (eventId: string) => {
            try {
              await axios.post(`${API}/api/events/${eventId}/join`, {}, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
              });
              const refreshed = await axios.get(`${API}/api/events/${eventId}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
              });
              setSelectedEvent(refreshed.data);
            } catch (err) {
              console.error('Join error:', err);
            }
          }}
          onMessage={(organizerId: string) => {
            try { localStorage.setItem('auralink-open-chat-with', organizerId); } catch {}
            onNavigate && onNavigate('dashboard');
          }}
          onViewProfile={(userId: string) => {
            const user = selectedEvent?.organizer ? { ...selectedEvent.organizer, _id: selectedEvent.organizer._id } : null;
            if (user) {
              (onShowProfile as any)?.(user);
            }
          }}
          currentUserId={currentUserId}
        />
      )}

      {selectedService && (
        <ServiceDetailModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
          onMessage={(providerId: string) => {
            try { localStorage.setItem('auralink-open-chat-with', providerId); } catch {}
            onNavigate && onNavigate('dashboard');
          }}
          onLike={async (serviceId: string) => {
            try {
              await axios.post(`${API}/api/services/${serviceId}/like`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
              const refreshed = await axios.get(`${API}/api/services/${serviceId}`);
              setSelectedService(refreshed.data);
            } catch (e) {
              console.error('Failed to like service', e);
            }
          }}
          currentUserId={currentUserId}
        />
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onMessage={(sellerId: string) => {
            try { localStorage.setItem('auralink-open-chat-with', sellerId); } catch {}
            onNavigate && onNavigate('dashboard');
          }}
          onLike={async (productId: string) => {
            try {
              const res = await axios.post(`${API}/api/marketplace/${productId}/like`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
              setSelectedProduct(res.data);
            } catch (e) {
              console.error('Failed to like product', e);
            }
          }}
          currentUserId={currentUserId}
        />
      )}

      {selectedVenue && (
        <VenueDetailModal
          venue={selectedVenue}
          token={token}
          onClose={() => setSelectedVenue(null)}
        />
      )}

      {showCreateEvent && (
        <CreateEventModal
          isOpen={showCreateEvent}
          onClose={() => setShowCreateEvent(false)}
          token={token}
          onSuccess={async () => {
            try { await loadEventFeed(true); } catch {}
            setShowCreateEvent(false);
          }}
          editingEvent={null}
          initialToken={undefined}
        />
      )}
    </div>
  );
}

function getCurrentPosition(opts?: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}

function deriveGeoErrorMessage(err: any, permissionState: PermissionState | null): string {
  try {
    if (!window.isSecureContext) return 'Location requires HTTPS or localhost.';
  } catch {}
  if (permissionState === 'denied') return 'Location permission denied in browser settings.';
  if (err?.code === 1) return 'Permission denied. Please allow location access.';
  if (err?.code === 2) return 'Position unavailable. Try again.';
  if (err?.code === 3) return 'Location timed out. Try again.';
  return 'Unable to get your location.';
}

async function getApproximateLocation(): Promise<{ lat: number; lng: number } | null> {
  // Try multiple public IP geolocation services; return first success
  try {
    const resp = await fetch('https://ipapi.co/json/');
    if (resp.ok) {
      const data = await resp.json();
      const lat = typeof data.latitude === 'number' ? data.latitude : Number(data.latitude);
      const lng = typeof data.longitude === 'number' ? data.longitude : Number(data.longitude);
      if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }
  } catch {}
  try {
    const resp = await fetch('https://ipinfo.io/json');
    if (resp.ok) {
      const data = await resp.json();
      if (typeof data.loc === 'string' && data.loc.includes(',')) {
        const [latStr, lngStr] = data.loc.split(',');
        const lat = Number(latStr);
        const lng = Number(lngStr);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
      }
    }
  } catch {}
  try {
    const resp = await fetch('https://freegeoip.app/json/');
    if (resp.ok) {
      const data = await resp.json();
      const lat = Number(data.latitude);
      const lng = Number(data.longitude);
      if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }
  } catch {}
  return null;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number { return deg * Math.PI / 180; }

async function reverseGeocodeCity(lat: number, lng: number, cache: Map<string, string>): Promise<string | null> {
  const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
  if (cache.has(key)) return cache.get(key) || null;
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&format=json&zoom=10&addressdetails=1`;
    const resp = await fetch(url, { headers: { 'Accept-Language': 'en' } });
    if (!resp.ok) return null;
    const data = await resp.json();
    const addr = data?.address || {};
    const city = addr.city || addr.town || addr.village || addr.hamlet || addr.county || addr.state || null;
    if (city) cache.set(key, city);
    return city;
  } catch {
    return null;
  }
}

function queueReverseGeocode(
  id: string,
  lat: number,
  lng: number,
  cache: Map<string, string>,
  setMap: React.Dispatch<React.SetStateAction<Record<string, string>>>,
) {
  if (!id) return;
  // Avoid duplicate inflight
  reverseGeocodeCity(lat, lng, cache)
    .then((city) => {
      if (!city) return;
      setMap((m) => (m[id] ? m : { ...m, [id]: city }));
    })
    .catch(() => {});
}

function formatLocationText(loc: any): string {
  try {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    const byText = loc.city || loc.name || loc.address;
    if (byText) return String(byText);
    const c = loc.coordinates;
    if (c && typeof c.lat === 'number' && typeof c.lng === 'number') {
      return `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
    }
    return '';
  } catch {
    return '';
  }
}

